import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Wallet, Users } from 'lucide-react'
import toast from 'react-hot-toast'
import cd2bReventaService from '@/services/cd2bReventaService'
import carteraService from '@/services/carteraService'
import commissionService from '@/services/commissionService'
import { fmtMoneda } from '@/lib/cd2bPricing'
import { useReventaHabilitada } from '@/hooks/useReventaHabilitada'
import PageContainer from '@/components/common/PageContainer'
import PageHeader from '@/components/common/PageHeader'
import BannerGatingReventa from '@/components/cd2b/BannerGatingReventa'
import KpiCard from '@/components/KpiCard'
import DataTable from '@/components/common/DataTable'
import { EmptyState } from '@/components/common/EmptyState'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'

const MINIMO_RETIRO_CENTS = 10000 // $100.00 — mismo piso que el mockup; ajustable si el negocio cambia el mínimo.

const ESTADO_LIQ = {
  pendiente: { label: 'Pendiente', variant: 'warning' },
  aprobada: { label: 'Aprobada', variant: 'success' },
  en_pago: { label: 'En pago', variant: 'default' },
  pagada: { label: 'Pagada', variant: 'success' },
  retenida: { label: 'Retenida · verificar', variant: 'danger' },
  anulada: { label: 'Anulada', variant: 'secondary' },
}
const statusVariantsComision = { pending: 'warning', pending_approval: 'warning', approved: 'default', paid: 'success', cancelled: 'danger', rejected: 'danger' }

/**
 * Mis ingresos (docs/120 §7.6): dos pestañas — Comisiones (programa de referidos, SIN CAMBIOS —
 * mismo `commissionService` que ya usa `/commissions`, aquí solo un resumen de solo lectura con
 * link a la página completa) y Margen de reventa (liquidaciones + saldo + solicitar pago, NUEVO).
 */
export default function ReventaIngresos() {
  const navigate = useNavigate()
  const habilitado = useReventaHabilitada()
  const [cargando, setCargando] = useState(true)
  const [saldo, setSaldo] = useState(null)
  const [liquidaciones, setLiquidaciones] = useState([])
  const [cartera, setCartera] = useState([])
  const [comisiones, setComisiones] = useState([])
  const [modalPago, setModalPago] = useState(false)
  const [notas, setNotas] = useState('')
  const [codigo2fa, setCodigo2fa] = useState('')
  const [solicitando, setSolicitando] = useState(false)

  const cargar = useCallback(async () => {
    setCargando(true)
    try {
      const [s, liq, cart, com] = await Promise.all([
        cd2bReventaService.getSaldo().catch(() => null),
        cd2bReventaService.listarLiquidaciones({ limit: 50 }).catch(() => null),
        carteraService.getCartera().catch(() => null),
        commissionService.getCommissions({}, { limit: 5 }).catch(() => null),
      ])
      setSaldo(s?.data || null)
      setLiquidaciones(liq?.data?.liquidaciones || [])
      setCartera(cart?.data?.items || [])
      setComisiones(com?.commissions || [])
    } catch {
      toast.error('No se pudieron cargar tus ingresos')
    } finally {
      setCargando(false)
    }
  }, [])

  useEffect(() => { cargar() }, [cargar])

  const nombrePorSub = (subId) => cartera.find((c) => String(c.subscriptionId) === String(subId))?.empresaNombre || 'Empresa'
  const edicionPorSub = (subId) => cartera.find((c) => String(c.subscriptionId) === String(subId))?.edicion || ''

  const margenPendiente = saldo?.margenPendienteCents || 0
  const puedeRetirar = margenPendiente >= MINIMO_RETIRO_CENTS

  const solicitarPago = async () => {
    setSolicitando(true)
    try {
      await cd2bReventaService.solicitarPayout(notas, codigo2fa)
      toast.success('Pago solicitado. Un administrador lo revisará.')
      setModalPago(false)
      setNotas('')
      setCodigo2fa('')
      cargar()
    } catch (e) {
      toast.error(e?.response?.data?.error || 'No se pudo solicitar el pago')
    } finally {
      setSolicitando(false)
    }
  }

  const cifra = (cents, moneda) => fmtMoneda((cents || 0) / 100, moneda)
  const numero = 'font-mono tabular-nums text-foreground'

  const columnasLiq = [
    {
      clave: 'cliente', titulo: 'Cliente', prioridad: 'titulo',
      celda: (l) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-foreground">{nombrePorSub(l.subscriptionId)}</p>
          <p className="text-caption text-subtle">{edicionPorSub(l.subscriptionId)}</p>
        </div>
      ),
    },
    { clave: 'periodo', titulo: 'Periodo', prioridad: 'meta', celda: (l) => <span className="text-subtle">{new Date(l.createdAt).toLocaleDateString('es-SV', { month: 'short', year: 'numeric' })}</span> },
    { clave: 'cobrado', titulo: 'Cobrado', alineacion: 'derecha', prioridad: 'meta', celda: (l) => <span className={numero}>{cifra(l.montos?.cobradoCents, l.montos?.currency)}</span> },
    { clave: 'base', titulo: 'Base CoreDesk', alineacion: 'derecha', prioridad: 'meta', celda: (l) => <span className={numero}>{cifra(l.montos?.baseCents, l.montos?.currency)}</span> },
    {
      clave: 'margen', titulo: 'Tu margen', alineacion: 'derecha', prioridad: 'valor',
      celda: (l) => <span className="font-mono font-semibold tabular-nums text-accent-700 dark:text-accent-300">{cifra(l.montos?.margenCents, l.montos?.currency)}</span>,
    },
    {
      clave: 'estado', titulo: 'Estado', prioridad: 'valor',
      celda: (l) => {
        const est = ESTADO_LIQ[l.estado] || { label: l.estado, variant: 'secondary' }
        return <Badge variant={est.variant}>{est.label}</Badge>
      },
    },
  ]

  const columnasComision = [
    { clave: 'referido', titulo: 'Referido', prioridad: 'titulo', celda: (c) => <p className="truncate text-foreground">{c.referralId?.referredUser?.email || 'N/A'}</p> },
    { clave: 'comision', titulo: 'Comisión', alineacion: 'derecha', prioridad: 'valor', celda: (c) => <span className={numero}>{commissionService.formatCurrency(c.amount)}</span> },
    {
      clave: 'estado', titulo: 'Estado', prioridad: 'valor',
      celda: (c) => <Badge variant={statusVariantsComision[c.status] || 'secondary'}>{commissionService.getStatusLabel(c.status)}</Badge>,
    },
  ]

  if (cargando) {
    return (
      <PageContainer>
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64" />
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <PageHeader title="Mis ingresos" description="Dos programas, dos saldos: comisión por referir (sin cambios) y margen por revender cd-2b." />

      <Tabs defaultValue="reventa">
        <TabsList className="max-w-full">
          <TabsTrigger value="reventa">Margen de reventa</TabsTrigger>
          <TabsTrigger value="comisiones">Comisiones (referidos)</TabsTrigger>
        </TabsList>

        <TabsContent value="reventa" className="space-y-6">
          {!habilitado && <BannerGatingReventa variante="ingresos" />}

          <div className="grid gap-3 sm:grid-cols-3">
            <KpiCard title="Saldo pendiente" value={fmtMoneda(margenPendiente / 100)} tone="accent"
              delta={`${liquidaciones.filter((l) => l.estado === 'aprobada').length} liquidaciones aprobadas`} />
            <KpiCard title="Pagado histórico" value={fmtMoneda(liquidaciones.filter((l) => l.estado === 'pagada').reduce((s, l) => s + (l.montos?.margenCents || 0), 0) / 100)} tone="primary" />
            <KpiCard title="Mínimo para retirar" value={fmtMoneda(MINIMO_RETIRO_CENTS / 100)} tone={puedeRetirar ? 'success' : 'neutral'}
              delta={puedeRetirar ? 'Ya lo superaste' : 'Todavía no'} />
          </div>

          <DataTable
            etiquetaTabla="Liquidaciones de margen"
            columnas={columnasLiq}
            filas={liquidaciones}
            claveDeFila={(l) => l._id}
            tarjetasEnMovil
            className="bg-card"
            vacio={<EmptyState icono={<Wallet size={24} />} titulo="Todavía no tienes liquidaciones de margen" descripcion="Aparecen cuando un cliente tuyo en modo reventa paga su mensualidad." />}
          />

          <Button className="w-full sm:w-auto" disabled={!puedeRetirar} onClick={() => setModalPago(true)}>
            Solicitar pago de {fmtMoneda(margenPendiente / 100)}
          </Button>
        </TabsContent>

        <TabsContent value="comisiones" className="space-y-4">
          <p className="text-caption text-subtle">Programa de referidos general de CoreDesk — sin cambios en este rediseño.</p>
          <DataTable
            etiquetaTabla="Últimas comisiones"
            columnas={columnasComision}
            filas={comisiones}
            claveDeFila={(c) => c._id}
            tarjetasEnMovil
            className="bg-card"
            vacio={<EmptyState icono={<Users size={24} />} titulo="Sin comisiones registradas" />}
          />
          <Button variant="outline" className="w-full sm:w-auto" onClick={() => navigate('/commissions')}>Ver todas mis comisiones</Button>
        </TabsContent>
      </Tabs>

      <Dialog open={modalPago} onOpenChange={setModalPago}>
        <DialogContent>
          <DialogHeader><DialogTitle>Solicitar pago de margen</DialogTitle></DialogHeader>
          <p className="text-body-sm text-subtle">
            Vas a solicitar el pago de <strong>{fmtMoneda(margenPendiente / 100)}</strong> en margen de reventa pendiente.
            Un administrador de CoreDesk lo revisa antes de transferir.
          </p>
          <Textarea placeholder="Notas (opcional)" value={notas} onChange={(e) => setNotas(e.target.value)} />
          <Input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={8}
            placeholder="Código 2FA (si lo tienes activado)"
            aria-label="Código de tu app autenticadora"
            value={codigo2fa}
            onChange={(e) => setCodigo2fa(e.target.value)}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalPago(false)}>Cancelar</Button>
            <Button onClick={solicitarPago} disabled={solicitando}>Confirmar solicitud</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  )
}
